<script setup lang="ts" generic="MODE extends Exclude<TProgramMetaMode, 'PROGRAMMING'>">
import { computed } from "vue";

import ComfortIcon from "@/assets/icons/programs/comfort.svg?component";
import EcoIcon from "@/assets/icons/programs/eco.svg?component";
import FrostIcon from "@/assets/icons/programs/frost.svg?component";
import OnIcon from "@/assets/icons/programs/on.svg?component";
import TempIcon from "@/assets/icons/programs/temperature.svg?component";
import {
  EBookableSpaceMetaMode,
  ECommonProgramMetaMode,
  EHeaterProgramMetaMode,
  EOnProgramMetaMode,
  TProgramMetaMode,
} from "@/domains/v4b/program/subprogram.types";
import { useTypedIntl } from "@/locales/typed-intl";

interface IProps {
  mode: MODE;
}

interface IEmits {
  (e: "click", payload: MODE): void;
}

const props = defineProps<IProps>();
const emit = defineEmits<IEmits>();
const { t, k } = useTypedIntl();

const selected = defineModel<boolean>("modelValue", {
  required: false,
  default: false,
});

function handleClick() {
  emit("click", props.mode);
}

const isComfort = computed(() => {
  return props.mode === EHeaterProgramMetaMode.COMFORT;
});
const isNoFrost = computed(() => {
  return props.mode === EHeaterProgramMetaMode.NO_FROST;
});
const isTemp = computed(() => {
  return props.mode === ECommonProgramMetaMode.TEMP;
});
const isEco = computed(() => {
  return props.mode === EHeaterProgramMetaMode.ECO;
});
const isOff = computed(() => {
  return props.mode === ECommonProgramMetaMode.OFF;
});
const isOn = computed(() => {
  return props.mode === EOnProgramMetaMode.ON;
});
const isAvailable = computed(() => {
  return props.mode === EBookableSpaceMetaMode.AVAILABLE;
});
const isOccupied = computed(() => {
  return props.mode === EBookableSpaceMetaMode.OCCUPIED;
});
</script>

<template>
  <div
    :aria-label="props.mode"
    class="w-full h-[92px] border border-gray-200 rounded-lg flex flex-col items-center justify-center"
    :class="{
      '!border-2 !border-brand': selected,
    }"
    role="button"
    tabindex="0"
    @click="handleClick"
    @keydown.enter="handleClick"
  >
    <div
      class="rounded-full h-[34px] w-[34px] bg-comfort text-programming-comfort-foreground flex items-center justify-center"
      :class="{
        'bg-programming-comfort-light text-programming-comfort-foreground': isComfort,
        'bg-programming-no-frost-light text-programming-no-frost-foreground': isNoFrost,
        'bg-programming-temperature-light text-programming-temperature-foreground': isTemp,
        'bg-programming-eco-light text-programming-eco-foreground': isEco,
        'bg-inverse text-programming-stop-foreground': isOff || isOccupied,
        'bg-programming-on-light text-programming-on-foreground': isOn || isAvailable,
      }"
    >
      <ComfortIcon
        v-if="isComfort"
        class="[&>path]:!stroke-programming-comfort-foreground"
      />
      <FrostIcon
        v-else-if="isNoFrost"
        class="[&>path]:!stroke-programming-frost-foreground"
      />
      <TempIcon
        v-else-if="isTemp"
        class="[&>path]:!stroke-programming-temp-foreground"
      />
      <EcoIcon
        v-else-if="isEco"
        class="[&>path]:!stroke-programming-eco-foreground"
      />
      <i
        v-else-if="isOff || isOccupied"
        class="pi pi-times-circle [&>*]:!stroke-programming-off-foreground"
      />
      <OnIcon
        v-else-if="isOn || isAvailable"
        class="[&>*]:!stroke-programming-on-foreground"
      />
    </div>
    <span class="text-[16px] font-semibold mt-1">
      {{ t(k.global.program.metaMode[props.mode]) }}
    </span>
  </div>
</template>

<style scoped lang="scss"></style>
